import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Tag, X } from "lucide-react";

// Customer-facing coupon entry. Validates against the backend and reports the
// applied coupon (with computed discount) back to the parent via onChange.
export default function CouponField({ amount, applied, onChange, context, testid = "coupon" }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // Auto-apply a coupon shared via ?coupon=CODE link (stored on landing).
  useEffect(() => {
    if (applied) return;
    let pending;
    try { pending = localStorage.getItem("cs_pending_coupon"); } catch { pending = null; }
    if (!pending) return;
    (async () => {
      try {
        const { data } = await api.post("/coupons/validate", { code: pending, amount, context });
        onChange(data);
        toast.success(`Coupon ${data.code} applied`);
        try { localStorage.removeItem("cs_pending_coupon"); } catch { /* ignore */ }
      } catch { /* wrong scope / expired — leave it for another checkout */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: c, amount, context });
      onChange(data);
      toast.success("Coupon applied");
      setCode("");
    } catch (e) {
      onChange(null);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const discount = applied?.discount_preview ?? (
    applied
      ? (applied.discount_type === "percent"
          ? Math.round(amount * (applied.discount_value / 100) * 100) / 100
          : Math.min(applied.discount_value, amount))
      : 0
  );

  if (applied) {
    return (
      <div data-testid={`${testid}-applied`} className="flex items-center justify-between gap-3 px-4 py-3 mb-4 border-2 border-[#C6FF33] bg-[#C6FF33]/20">
        <span className="inline-flex items-center gap-2 text-sm font-bold text-[#121212]">
          <Tag size={15} /> {applied.code}
          <span className="font-normal text-[#121212]/70">
            — {applied.discount_type === "percent" ? `${applied.discount_value}% off` : `$${applied.discount_value} off`}
            {discount ? ` (−$${discount.toFixed(2)})` : ""}
          </span>
        </span>
        <button data-testid={`${testid}-remove`} onClick={() => onChange(null)} className="text-[#121212]/60 hover:text-[#121212]"><X size={16} /></button>
      </div>
    );
  }

  return (
    <div className="flex gap-2 mb-4">
      <input
        data-testid={`${testid}-input`}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
        placeholder="Coupon code"
        className="flex-1 px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none uppercase font-mono text-sm"
      />
      <button
        data-testid={`${testid}-apply`}
        onClick={apply}
        disabled={busy || !code.trim()}
        className="px-5 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.2em] uppercase font-bold"
      >
        {busy ? "…" : "Apply"}
      </button>
    </div>
  );
}
